import React, { useMemo, useState } from "react";
import { ApplicationWrapper } from "wrapper/ApplicationWrapper";
import { ErrorModalWrapper } from "wrapper/ErrorModalWrapper";
import { ErrorBoundary } from "components/ErrorBoundary";
import { CommonContextWrapper } from "wrapper/CommonWrapper";
import ErrorModal from "layouts/ErrorModal";

import {
  setCsrfToken,
  getConfigExpiry,
  getPlatform,
  getRemoteConfig,
  discardCallbackWithTimeout,
} from "../helpers/index";

import RouteWithSubRoutes from "App/subRoute";
import RetinaAnalytics from "../components/Retina";
import { AppCallback, doAttachWebCallback, doCallAppCallback, handleReactBlankScreen, ICustomerConfig, isAppCallbackPresent, WebCallback } from "helpers/utils/webView";
import { logger } from "logging";
import { INFO_EVENTS } from "logging/newRelicLogger";
import { getAppVersion } from "helpers/appVersion";
import { isMobileApp } from "helpers/deviceType";
import { getScheduleIntentNotificationConfig } from "layouts/Payment/common/dependencies/dependencyhelpers";
import { useTheme } from "emotion-theming";
import { PAYMENT_FLOWS } from "constants/payment";
import { ExperimentAndSwitchContextWrapper } from "wrapper/ExperimentAndSwitchWrapper";
import { UserWrapper } from "wrapper/UserWrapper";


function InitiateRoutes({ routes }: { routes: [PageRoute] }) {
  const DEFAULT_POST_PAYMENT_BOTTOMSHEET_DELAY = 2000;

  const theme:Theme = useTheme();
  let isdoHandlePostPaymentBackCalled = false;
  let executerFunction = () => {};
  const remoteConfig = getRemoteConfig();
  const paymentConfigs = remoteConfig?.configs?.payment;
  const postPaymentBackBottomsheetDelayTimeoutInMs = remoteConfig?.configs?.postPaymentBackBottomsheetDelayTimeoutInMs || DEFAULT_POST_PAYMENT_BOTTOMSHEET_DELAY;
  const isAnalyticsV2Enabled = paymentConfigs?.retina?.analyticsV2Enabled;
  const analyticsEndpoint = paymentConfigs?.retina?.analyticsEndpoint;
  const configExpiry = getConfigExpiry();
  let platform = getPlatform();
  logger.info(INFO_EVENTS.PAGE_LOADED, {
    appVersion: getAppVersion(),
  });

  const [userData, setUserData] = useState<any>({});
  const [renderPage, setRenderPage] = useState(false);
  const isApp = isMobileApp();

  useMemo(() => {
    if (isApp) {
      handleReactBlankScreen();
      /**
       * This is getting customer info from apps
       */
      doAttachWebCallback({
        callbackName: WebCallback.GET_CUSTOMER_INFO,
        callbackDeclaration: (customerConfig: ICustomerConfig) => {
          logger.info(INFO_EVENTS.WEBVIEW_CALLBACK, { data:`getCustomerInfo callback called, ${customerConfig?.customerId}` });
          console.log("__customerConfig", customerConfig);
          /**
           * If formKey is not coming from apps end then we will log.
           */
          if (!customerConfig?.form_key) {
            const error = new Error("Unable to get user data from apps");
            logger.error(error);
          }
          const data = {
            result: {
              form_key: customerConfig?.form_key,
              customerData: {
                id: customerConfig?.customerId,
                loginEmail: customerConfig?.loginEmail,
                gfirstname: customerConfig?.firstname,
                glastname: customerConfig?.lastname,
                mobile: customerConfig?.mobile,
                is_loyal: customerConfig?.is_loyal,
              },
            },
          };

          if (customerConfig?.customerId) {
            setUserData(data);
          }
        },
      });

      const doHandlePostPaymentBack = () => {
        /** Delay the opening of bottomsheet by defined time */
        /* eslint-disable react-hooks/exhaustive-deps */
        if (!isdoHandlePostPaymentBackCalled) {
          isdoHandlePostPaymentBackCalled = true;
          executerFunction = discardCallbackWithTimeout(() => {
            window.location.replace("#disableBack");
          }, postPaymentBackBottomsheetDelayTimeoutInMs);
        }
        executerFunction();
      };

      /**
       * This is for handling device back button in webview
       */
      try {
        doAttachWebCallback({
          callbackName: WebCallback.DO_HANDLE_NATIVE_BACK,
          callbackDeclaration: (data: any) => {
            console.log("doHandleNativeBack callback called", data.url);
            const url = window.location.href;
            if (url.includes(PAYMENT_FLOWS.POST_PAYMENT)) {
              doHandlePostPaymentBack();
            } else {
              window.history.go(-1);
            }
          },
        });
      } catch (error) {
        logger.error(error, "Error in attaching doHandleNativeBack callback");
        window.history.go(-1);
      }


      const scheduleNotificationConfig = getScheduleIntentNotificationConfig();

      /**
       * Added 50ms timeout for onPageLoad Callback bcz of sometimes onPageLoad callback called before
       * attaching the getCustomerInfo Callback on window.
       */
      setTimeout(() => {
        doCallAppCallback({
          name: AppCallback.ON_PAGE_LOAD,
          data: {
            "scheduleNotification": scheduleNotificationConfig || {},
            "customerDataCallback": WebCallback.GET_CUSTOMER_INFO,
          },
        });
      }, 50);


      /**
       * Wait for getCustomerInfo Data for 1 sec and if data not coming from apps then we will call API.
       */
      setTimeout(() => {
        setRenderPage(true);
      }, 1000);
    }
  }, []);

  /**
   * If appCallback not present and we are not getting the form_key from apps side
   * then we will call the reactConfig API Call.
   */
  if (isAppCallbackPresent({ callbackName: AppCallback.ON_PAGE_LOAD }) && !userData?.result?.form_key && !renderPage) {
    return null;
  }

  return (
    <ErrorBoundary message="Failed to load Base route" theme={theme}>
      <ErrorModalWrapper>
        <ErrorModal />
        <ApplicationWrapper>
          <UserWrapper userData={userData} callback={setCsrfToken} platform={platform} configExpiry={configExpiry}>
            <ExperimentAndSwitchContextWrapper>
              <CommonContextWrapper>
                <RouteWithSubRoutes routes={routes} />
                {isAnalyticsV2Enabled && analyticsEndpoint && <RetinaAnalytics />}
              </CommonContextWrapper>
            </ExperimentAndSwitchContextWrapper>
          </UserWrapper>
        </ApplicationWrapper>
      </ErrorModalWrapper>
    </ErrorBoundary>
  );
}

export default InitiateRoutes;

